// ===== BEGIN FILE: src/components/AppShell.tsx =====
import * as React from 'react';
import {
  Sheet, Box, Typography, IconButton, Tooltip, Chip, Divider,
  List, ListItem, ListItemButton, ListItemDecorator, Dropdown, Menu, MenuButton, MenuItem,
  Drawer, ModalClose
} from '@mui/joy';
import Select from 'react-select'; // For person selector
import HomeIcon from '@mui/icons-material/Home';
import MenuIcon from '@mui/icons-material/Menu';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import GroupIcon from '@mui/icons-material/Group';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import TimelineIcon from '@mui/icons-material/Timeline';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import SettingsIcon from '@mui/icons-material/Settings';
import RefreshIcon from '@mui/icons-material/Refresh';
import DescriptionIcon from '@mui/icons-material/Description';
import InsightsIcon from '@mui/icons-material/Insights';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import ChatIcon from '@mui/icons-material/ChatBubbleOutline';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import { supabase } from '../utils/supabaseClient';
import NotificationCenter from './NotificationCenter';
import QuickAccessMenu from './QuickAccessMenu';
import type { 
  OpportunityNotification, 
  CautionNotification, 
  YogaNotification,
  PredictionNotification
} from './NotificationCenter';
import { useIsMobile } from '../hooks/useIsMobile';
import { getFeatureConfig } from '../config/mobileFeatures';

// Keep your extended union with lowercase keys
export type TabValue =
  | 'calculator'
  | 'dasha'
  | 'feeds'
  | 'chat'
  | 'family'
  | 'admin'
  | 'mychart';

type AppShellProps = {
  title?: string;
  userLabel?: string;
  userEmail?: string; // User's email for settings menu
  chartName?: string; // Name of the person whose chart is being viewed
  isAdmin: boolean;
  canUseCalculator: boolean;
  activeTab: TabValue;
  onTabChange: (t: TabValue) => void;
  onRefreshAccess?: () => void; // Callback for refresh access
  onStartTour?: () => void; // Callback to start tour for current tab
  onOpenVideoTutorial?: () => void; // Callback to open video tutorial for current tab (kept for compat)
  homeAudioGuide?: React.ReactNode; // Audio guide control rendered in the header action row
  children?: React.ReactNode;
  // Person selector props
  profileOptions?: Array<{ value: string; label: string }>;
  selectedProfileId?: string | null;
  onProfileChange?: (id: string | null) => void;
  // Notification props
  opportunities?: OpportunityNotification[];
  cautions?: CautionNotification[];
  yogas?: YogaNotification[];
  predictions?: PredictionNotification[];
  onNotificationNavigate?: (section: 'current-period' | 'yogas', itemId?: string) => void;
  birthDate?: Date; // For age-based notification ordering
  feedStats?: { total: number; positive: number; negative: number; neutral: number };
  // Mobile chat props
  onOpenMobileChat?: () => void; // Callback to open mobile chat interface
  // Quick Access props
  showQuickAccess?: boolean; // Whether to show Quick Access dropdown
  onQuickSwotAnalysis?: () => void;
  onQuickGetRemedies?: () => void;
  onQuickCurrentPeriodAnalysis?: () => void;
  onQuickLifeAreasDashboard?: () => void;
  onQuickYogasAnalysis?: () => void;
  onQuickDashaTimeline?: () => void;
  onQuickFutureEventPredictor?: () => void;
  onQuickPlanetImpact?: () => void;
  onQuickActivitiesBalance?: () => void;
  onQuickMarriageCompatibility?: () => void;
  onQuickFamilyScoreCard?: () => void;
  onQuickLifeEventFinder?: () => void;
  onQuickPersonalMuhurta?: () => void;
  onQuickHouseDashboard?: () => void;
  onQuickFloatingChart?: () => void; // Admin-only: Open floating chart
  onGenerateReport?: () => void; // Admin-only: Generate report
  // AI credits for non-admin badge display
  aiCreditsGeneral?: number | null;
  aiCreditsLifeArea?: number | null;
  // Chat credits top-up
  chatCreditsRemaining?: number | null;
  onBuyCredits?: () => void;
  // WhatsApp opt-in
  onOpenWhatsAppOptIn?: () => void;
  // Subscription modal
  onOpenMySubscription?: () => void;
  subscriptionLabel?: string; // e.g. 'My Free Trial' / 'My Silver Plan' / 'My Gold Plan'
  subscriptionPlan?: string | null;
  subscriptionDaysRemaining?: number | null;
};

type TabItem = {
  label: string;
  value: TabValue;
  icon: React.ReactElement;
  disabled?: boolean;
};

export default function AppShell({
  title = 'ShreemAstro.com',
  userLabel = 'Logged in',
  userEmail,
  chartName,
  isAdmin,
  canUseCalculator,
  activeTab,
  onTabChange,
  onRefreshAccess,
  onStartTour,
  onOpenVideoTutorial,
  homeAudioGuide,
  profileOptions,
  selectedProfileId,
  onProfileChange,
  opportunities = [],
  cautions = [],
  yogas = [],
  predictions = [],
  onNotificationNavigate,
  birthDate,
  feedStats,
  onOpenMobileChat,
  showQuickAccess = false,
  onQuickSwotAnalysis,
  onQuickGetRemedies,
  onQuickCurrentPeriodAnalysis,
  onQuickLifeAreasDashboard,
  onQuickYogasAnalysis,
  onQuickDashaTimeline,
  onQuickFutureEventPredictor,
  onQuickPlanetImpact,
  onQuickActivitiesBalance,
  onQuickMarriageCompatibility,
  onQuickFamilyScoreCard,
  onQuickLifeEventFinder,
  onQuickPersonalMuhurta,
  onQuickHouseDashboard,
  onQuickFloatingChart,
  onGenerateReport,
  aiCreditsGeneral,
  aiCreditsLifeArea,
  chatCreditsRemaining,
  onBuyCredits,
  onOpenWhatsAppOptIn,
  onOpenMySubscription,
  subscriptionLabel,
  subscriptionPlan,
  subscriptionDaysRemaining,
  children
}: AppShellProps) {
  // Detect mobile device
  const { isMobile, width } = useIsMobile();
  const features = getFeatureConfig(isMobile);
  
  // Mobile drawer state
  const [drawerOpen, setDrawerOpen] = React.useState(false);

  // Prevent browser-level scroll on the Ask Shreem tab
  React.useEffect(() => {
    if (activeTab === 'chat') {
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
    } else {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    }
    return () => {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    };
  }, [activeTab]);
  
  // Debug logging
  React.useEffect(() => {
    console.log('[AppShell Mobile Debug]', { 
      isMobile, 
      width, 
      enabledTabs: Object.entries(features.tabs).filter(([k, v]) => v).map(([k]) => k)
    });
  }, [isMobile, width, features]);

  // Build the main top-menu items in the new order (filtered by mobile/desktop)
  const allTabs: TabItem[] = [
    { label: 'Ask Shreem',              value: 'chat',          icon: <img src="/Logo.png" alt="" style={{ width: 20, height: 20, borderRadius: '50%' }} /> },
    { label: 'My Chart',                value: 'calculator',    icon: <InsightsIcon />,            disabled: !canUseCalculator },
    { label: 'Life Timing',             value: 'dasha',         icon: <TimelineIcon /> },
    { label: 'Yogas & Predictions',     value: 'feeds',         icon: <AutoAwesomeIcon /> },
    { label: 'Deep Insights',           value: 'mychart',       icon: <DescriptionIcon /> },
  ];

  // Filter tabs based on mobile/desktop configuration
  const tabs = allTabs.filter(tab => {
    switch (tab.value) {
      case 'calculator': return features.tabs.calculator;
      case 'dasha': return features.tabs.dasha;
      case 'feeds': return features.tabs.feeds;
      case 'mychart': return features.tabs.mychart;
      case 'chat': return features.tabs.chat;
      case 'family': return features.tabs.family;
      case 'admin': return features.tabs.admin;
      default: return true;
    }
  });

  // No longer adding Admin to main tabs - it's now in Settings dropdown

  return (
    <Box sx={{ 
      minHeight: '100dvh', 
      bgcolor: 'background.body', 
      display: 'flex', 
      flexDirection: 'column',
      width: '100%',
      maxWidth: '100vw',
      overflowX: 'hidden',
      overflow: activeTab === 'chat' ? 'hidden' : undefined,
    }}>
      {/* Top bar */}
      <Sheet
        variant="soft"
        color="primary"
        data-audio-guide-header
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 10001,
          backdropFilter: 'saturate(180%) blur(10px)',
          background: 'linear-gradient(135deg, #fff8f8 0%, #fdf0f2 50%, #fff5f6 100%)',
          borderBottom: '1px solid rgba(184,48,80,0.14)',
          width: '100%',
        }}
      >
        {/* Top bar with logo, title and user info */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1, sm: 2 },
            px: { xs: 1, sm: 1.5, md: 2 },
            py: 1,
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box',
            ...(isMobile && {
              flexWrap: 'nowrap',
            }),
          }}
        >
          {/* Left side: Logo + Title + Menu */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Logo - responsive size */}
            <Box
              component="img"
              src="/Logo.png"
              alt="ShreemAstro Logo"
              sx={{
                height: { xs: 40, sm: 60, md: 70, lg: 80 },
                width: { xs: 40, sm: 60, md: 70, lg: 80 },
                objectFit: 'contain',
                cursor: 'pointer',
                flexShrink: 0,
              }}
              onClick={() => onTabChange(isMobile ? 'calculator' : 'chat')}
            />

            {/* Title and Menu stacked vertically */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 0.5, sm: 1.5 }, flex: 1, minWidth: 0, overflow: 'hidden' }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.25 }}>
                <Typography
                  level="h3"
                  sx={{
                    fontFamily: '"Amita", serif',
                    fontWeight: 700,
                    fontSize: { xs: '1.5rem', md: '1.8rem' },
                    background: 'linear-gradient(135deg, #b83050 0%, #7a1a2e 45%, #4a0a18 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    cursor: 'pointer',
                    lineHeight: 1,
                    whiteSpace: 'nowrap',
                    letterSpacing: '1px',
                    textShadow: 'none',
                  }}
                  onClick={() => onTabChange(isMobile ? 'calculator' : 'chat')}
                >
                  ShreemAstro
                  <Box
                    component="sup"
                    sx={{
                      fontSize: '0.6em',
                      fontWeight: 400,
                      letterSpacing: 0,
                      verticalAlign: 'super',
                      lineHeight: 0,
                      WebkitTextFillColor: 'rgba(184,48,80,1)',
                    }}
                  >
                    ™
                  </Box>
                </Typography>
                <Typography
                  sx={{
                    display: { xs: 'none', md: 'block' },
                    fontSize: '0.8rem',
                    fontWeight: 500,
                    fontFamily: '"Cormorant Garamond", "Garamond", serif',
                    fontStyle: 'italic',
                    color: 'rgba(122, 26, 46, 0.60)',
                    whiteSpace: 'nowrap',
                    letterSpacing: '0.02em',
                    lineHeight: 1,
                  }}
                >
                  — Ancient Wisdom. Intelligent Technology.
                </Typography>
              </Box>

              {/* Mobile: Profile Selector under title */}
              {isMobile && canUseCalculator && profileOptions && profileOptions.length > 0 && (
                <Box sx={{ 
                  minWidth: 80, 
                  maxWidth: 200, 
                  flex: '0 1 auto',
                }}>
                  <Select
                    options={profileOptions}
                    value={profileOptions.find(o => o.value === selectedProfileId) ?? null}
                    onChange={(opt) => {
                      if (opt && onProfileChange) onProfileChange(opt.value);
                    }}
                    placeholder="Select person"
                    isClearable={false}
                    isSearchable={true}
                    menuPortalTarget={document.body}
                    styles={{
                      control: (base) => ({
                        ...base,
                        minHeight: '32px',
                        height: '32px',
                        fontSize: '0.75rem',
                        fontFamily: '"Cinzel", serif',
                        letterSpacing: '0.03em',
                        backgroundColor: 'var(--joy-palette-background-surface)',
                        borderColor: 'var(--joy-palette-neutral-outlinedBorder)',
                      }),
                      option: (base) => ({
                        ...base,
                        fontFamily: '"Cinzel", serif',
                        fontSize: '0.75rem',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }),
                      singleValue: (base) => ({
                        ...base,
                        fontFamily: '"Cinzel", serif',
                      }),
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 10002
                      }),
                      menu: (base) => ({ 
                        ...base, 
                        fontSize: '0.75rem'
                      }),
                      valueContainer: (base) => ({
                        ...base,
                        padding: '0 4px'
                      }),
                      indicatorsContainer: (base) => ({
                        ...base,
                        height: '32px'
                      })
                    }}
                  />
                </Box>
              )}

              {/* Menu bar below title - Desktop only: horizontal tabs */}
              {!isMobile && (
                <Box
                  sx={{
                    display: 'flex',
                    gap: 0.75,
                    flexWrap: 'nowrap',
                    alignItems: 'center',
                  }}
                >
                  {tabs.map(t => {
                    const selected = activeTab === t.value;
                    return (
                      <React.Fragment key={t.value}>
                        <Box
                          component="button"
                          onClick={() => !t.disabled && onTabChange(t.value)}
                          disabled={t.disabled}
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                            cursor: t.disabled ? 'not-allowed' : 'pointer',
                            fontFamily: '"Cinzel", "Garamond", serif',
                            fontWeight: selected ? 600 : 400,
                            fontSize: { xs: '0.78rem', sm: '0.8125rem' },
                            letterSpacing: '0.04em',
                            px: 1.25,
                            py: 0.5,
                            borderRadius: '999px',
                            transition: 'all 0.15s',
                            outline: 'none',
                            ...(selected ? {
                              background: 'linear-gradient(135deg, #b83050 0%, #8b1a30 100%)',
                              color: '#fff',
                              border: 'none',
                            } : {
                              background: 'rgba(184,48,80,0.08)',
                              color: '#7a1a2e',
                              border: '1px solid rgba(184,48,80,0.22)',
                              '&:hover': { background: 'rgba(184,48,80,0.15)', color: '#5a0f20' },
                            }),
                          }}
                        >
                          {t.icon && <Box component="span" sx={{ display: 'inline-flex', fontSize: '1em' }}>{t.icon}</Box>}
                          {t.label}
                        </Box>

                      </React.Fragment>
                    );
                  })}
                </Box>
              )}
            </Box>
          </Box>

          {/* Spacer - reduce space to push icons left */}
          <Box sx={{ flex: isMobile ? 0 : 1, minWidth: isMobile ? 0 : undefined }} />

          {/* Right side: Mobile (icons in same row) vs Desktop (full user info) */}
          {isMobile ? (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 0.5, 
              flexShrink: 0,
            }}>
              {/* Mobile: Chat Icon */}
              {onOpenMobileChat && (
                <IconButton
                  variant="soft"
                  color="neutral"
                  onClick={onOpenMobileChat}
                  size="sm"
                  sx={{ 
                    borderRadius: 'md',
                    flexShrink: 0,
                    minWidth: '32px',
                    minHeight: '32px',
                    bgcolor: 'rgba(184,48,80,0.1)',
                    color: '#b83050',
                    '&:hover': { bgcolor: 'rgba(184,48,80,0.18)' },
                  }}
                >
                  <ChatIcon sx={{ fontSize: '1rem' }} />
                </IconButton>
              )}
              
              {/* Mobile: Notifications */}
              {onNotificationNavigate && (
                <Box sx={{ 
                  '& > div': { 
                    '& button': { 
                      minWidth: '32px !important', 
                      minHeight: '32px !important',
                      padding: '6px !important'
                    } 
                  } 
                }}>
                  <NotificationCenter
                    opportunities={opportunities}
                    cautions={cautions}
                    yogas={yogas}
                    predictions={predictions}
                    onNavigate={onNotificationNavigate}
                    birthDate={birthDate}
                    feedStats={feedStats}
                  />
                </Box>
              )}
              
              {/* Mobile: Quick Access Menu */}
              {showQuickAccess && onQuickSwotAnalysis && onQuickGetRemedies && onQuickCurrentPeriodAnalysis && (
                <QuickAccessMenu
                  onSwotAnalysis={onQuickSwotAnalysis}
                  onGetRemedies={onQuickGetRemedies}
                  onCurrentPeriodAnalysis={onQuickCurrentPeriodAnalysis}
                  onLifeAreasDashboard={onQuickLifeAreasDashboard}
                  onYogasAnalysis={onQuickYogasAnalysis}
                  onDashaTimeline={onQuickDashaTimeline}
                  onFutureEventPredictor={onQuickFutureEventPredictor}
                  onPlanetImpact={onQuickPlanetImpact}
                  onActivitiesBalance={onQuickActivitiesBalance}
                  onMarriageCompatibility={onQuickMarriageCompatibility}
                  onFamilyScoreCard={onQuickFamilyScoreCard}
                  onLifeEventFinder={onQuickLifeEventFinder}
                  onPersonalMuhurta={onQuickPersonalMuhurta}
                  onHouseDashboard={onQuickHouseDashboard}
                  onFloatingChart={onQuickFloatingChart}
                  isAdmin={isAdmin}
                  aiCreditsGeneral={aiCreditsGeneral}
                  aiCreditsLifeArea={aiCreditsLifeArea}
                  disabled={!showQuickAccess}
                  isMobile={true}
                />
              )}
              
              {/* Mobile: Settings Menu */}
              <IconButton
                variant="soft"
                color="neutral"
                onClick={() => setDrawerOpen(true)}
                size="sm"
                sx={{ 
                  borderRadius: 'md',
                  flexShrink: 0,
                  minWidth: '32px',
                  minHeight: '32px',
                  bgcolor: 'rgba(184,48,80,0.1)',
                  color: '#b83050',
                  '&:hover': { bgcolor: 'rgba(184,48,80,0.18)' },
                }}
              >
                <SettingsOutlinedIcon sx={{ fontSize: '1rem' }} />
              </IconButton>
            </Box>
          ) : (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'flex-end', 
              gap: 0.5,
              flexShrink: 0,
              minWidth: 0,
            }}>
              {/* Desktop: Person selector with Audio Guide positioned to its left */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, position: 'relative' }}>
                {canUseCalculator && profileOptions && profileOptions.length > 0 && (
                <Box sx={{ width: 240, height: 28, position: 'relative' }}>
                  {/* Audio Guide anchored visually left of the selector without expanding column width */}
                  {homeAudioGuide && (
                    <Box sx={{ position: 'absolute', right: 'calc(100% + 8px)', top: '50%', transform: 'translateY(-50%)', whiteSpace: 'nowrap' }}>
                      {homeAudioGuide}
                    </Box>
                  )}
                  <Select
                    options={profileOptions}
                    value={profileOptions.find(o => o.value === selectedProfileId) ?? null}
                    onChange={(opt) => {
                      const id = opt ? String((opt as any).value) : null;
                      onProfileChange?.(id);
                    }}
                    placeholder="Select person..."
                    isClearable
                    styles={{
                      control: (base) => ({ 
                        ...base, 
                        minHeight: '28px',
                        height: '28px',
                        fontSize: '0.8rem',
                        fontFamily: '"Cinzel", serif',
                        letterSpacing: '0.03em',
                        backgroundColor: 'var(--joy-palette-background-surface)',
                        borderColor: 'var(--joy-palette-neutral-outlinedBorder)',
                        width: '240px'
                      }),
                      menu: (base) => ({ 
                        ...base, 
                        fontSize: '0.8rem',
                        fontFamily: '"Cinzel", serif',
                        zIndex: 10000
                      }),
                      option: (base) => ({
                        ...base,
                        fontFamily: '"Cinzel", serif',
                        fontSize: '0.8rem',
                        letterSpacing: '0.02em',
                      }),
                      singleValue: (base) => ({
                        ...base,
                        fontFamily: '"Cinzel", serif',
                      }),
                      valueContainer: (base) => ({
                        ...base,
                        padding: '0 6px'
                      }),
                      indicatorsContainer: (base) => ({
                        ...base,
                        height: '28px'
                      })
                    }}
                  />
                </Box>
              )}
              </Box>

              {/* Bottom row: Quick Access + Video + Notifications + Settings */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 0.75, 
                flexWrap: 'nowrap',
                justifyContent: 'flex-end',
                minWidth: 0,
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {/* Quick Access Menu - Below profile selector */}
                  {showQuickAccess && onQuickSwotAnalysis && onQuickGetRemedies && onQuickCurrentPeriodAnalysis && (
                    <Box data-audio-cue="home-quick-access" data-audio-scroll="top" sx={{ display: 'contents' }}>
                    <QuickAccessMenu
                      onSwotAnalysis={onQuickSwotAnalysis}
                      onGetRemedies={onQuickGetRemedies}
                      onCurrentPeriodAnalysis={onQuickCurrentPeriodAnalysis}
                      onLifeAreasDashboard={onQuickLifeAreasDashboard}
                      onYogasAnalysis={onQuickYogasAnalysis}
                      onDashaTimeline={onQuickDashaTimeline}
                      onFutureEventPredictor={onQuickFutureEventPredictor}
                      onPlanetImpact={onQuickPlanetImpact}
                      onActivitiesBalance={onQuickActivitiesBalance}
                      onMarriageCompatibility={onQuickMarriageCompatibility}
                      onFamilyScoreCard={onQuickFamilyScoreCard}
                      onLifeEventFinder={onQuickLifeEventFinder}
                      onPersonalMuhurta={onQuickPersonalMuhurta}
                      onHouseDashboard={onQuickHouseDashboard}
                      onFloatingChart={onQuickFloatingChart}
                      isAdmin={isAdmin}
                      aiCreditsGeneral={aiCreditsGeneral}
                      aiCreditsLifeArea={aiCreditsLifeArea}
                      disabled={false}
                    />
                    </Box>
                  )}
                  
                  {/* Notification Center Bell Icon */}
                  {onNotificationNavigate && (
                    <Box data-audio-cue="home-bell-icon" data-audio-scroll="top" sx={{ display: 'contents' }}>
                    <NotificationCenter
                      opportunities={opportunities}
                      cautions={cautions}
                      yogas={yogas}
                      predictions={predictions}
                      onNavigate={onNotificationNavigate}
                      birthDate={birthDate}
                      feedStats={feedStats}
                      subscriptionPlan={subscriptionPlan}
                      subscriptionDaysRemaining={subscriptionDaysRemaining}
                      onOpenSubscription={onOpenMySubscription}
                    />
                    </Box>
                  )}

                  {/* Home Audio Guide / Video Tutorial */}
                  {onOpenVideoTutorial && (
                    <Tooltip title="Watch video tutorial for this page" variant="soft" placement="bottom">
                      <IconButton
                        variant="soft"
                        color="success"
                        size="sm"
                        onClick={onOpenVideoTutorial}
                        sx={{
                          animation: 'pulse 2s infinite',
                          '@keyframes pulse': {
                            '0%, 100%': { opacity: 1 },
                            '50%': { opacity: 0.7 },
                          },
                        }}
                      >
                        <PlayCircleOutlineIcon sx={{ fontSize: '1.3rem' }} />
                      </IconButton>
                    </Tooltip>
                  )}


                  {/* User Settings Dropdown Menu */}
                  <Dropdown>
                    <MenuButton
                      slots={{ root: IconButton }}
                      slotProps={{ root: { variant: 'soft', color: 'neutral', size: 'sm' } }}
                    >
                      <SettingsIcon sx={{ fontSize: '1.1rem' }} />
                    </MenuButton>
                  <Menu placement="bottom-end" sx={{ minWidth: 240, zIndex: 10002 }}>
                    {/* User info header */}
                    <MenuItem disabled sx={{ bgcolor: 'rgba(184,48,80,0.07)', opacity: 1, cursor: 'default', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
                      <Typography level="body-sm" sx={{ fontWeight: 600, fontFamily: '"Cinzel", serif', letterSpacing: '0.04em', color: '#7a1a2e' }}>
                        {userLabel}
                      </Typography>
                      {userEmail && (
                        <Typography level="body-xs" sx={{ fontFamily: '"Cormorant Garamond", serif', fontSize: '0.8rem', color: '#7a1a2e', opacity: 0.8 }}>
                          {userEmail}
                        </Typography>
                      )}
                    </MenuItem>
                    <Divider />
                    {onOpenMySubscription && (
                      <MenuItem onClick={onOpenMySubscription}>
                        <ListItemDecorator>
                          <WorkspacePremiumIcon sx={{ fontSize: '1.1rem', color: '#DAA520' }} />
                        </ListItemDecorator>
                        {subscriptionLabel ?? 'My Subscription'}
                      </MenuItem>
                    )}
                    {onOpenWhatsAppOptIn && (
                      <MenuItem onClick={onOpenWhatsAppOptIn}>
                        <ListItemDecorator>
                          <WhatsAppIcon sx={{ fontSize: '1.1rem', color: '#25D366' }} />
                        </ListItemDecorator>
                        WhatsApp Notifications
                      </MenuItem>
                    )}
                    <Divider />
                    {onStartTour && (
                      <MenuItem onClick={onStartTour}>
                        <ListItemDecorator>
                          <HelpOutlineIcon fontSize="small" />
                        </ListItemDecorator>
                        Navigation Help
                      </MenuItem>
                    )}
                    <MenuItem onClick={() => onTabChange('family')}>
                      <ListItemDecorator>
                        <GroupIcon fontSize="small" />
                      </ListItemDecorator>
                      Family Manager
                    </MenuItem>
                    {isAdmin && (
                      <MenuItem onClick={() => onTabChange('admin')}>
                        <ListItemDecorator>
                          <AdminPanelSettingsIcon fontSize="small" />
                        </ListItemDecorator>
                        Admin Console
                      </MenuItem>
                    )}
                    {isAdmin && onGenerateReport && (
                      <MenuItem onClick={onGenerateReport}>
                        <ListItemDecorator>
                          <DescriptionIcon fontSize="small" />
                        </ListItemDecorator>
                        Generate Report (Beta)
                      </MenuItem>
                    )}
                    {onRefreshAccess && (
                      <MenuItem onClick={onRefreshAccess}>
                        <ListItemDecorator>
                          <RefreshIcon fontSize="small" />
                        </ListItemDecorator>
                        Refresh Access
                      </MenuItem>
                    )}
                    <Divider />
                    <MenuItem onClick={() => supabase.auth.signOut()} color="danger">
                      <ListItemDecorator>
                        <LogoutIcon sx={{ fontSize: '1.1rem' }} />
                      </ListItemDecorator>
                      Sign Out
                    </MenuItem>
                  </Menu>
                  </Dropdown>
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </Sheet>

      {/* Main content area */}
      <Box
        component="main"
        sx={{
          flex: '1 1 auto',
          minWidth: 0,
          width: '100%',
          maxWidth: '100vw',
          overflowX: 'hidden',
          boxSizing: 'border-box',
          pl: activeTab === 'chat' ? 0 : { xs: 1, sm: 1.5, md: 2 },
          pr: activeTab === 'chat' ? 0 : { xs: 2, sm: 1.5, md: 2 },
          pt: { xs: '80px', sm: '90px', md: '100px' },
          pb: activeTab === 'chat' ? 0 : { xs: '70px', sm: 2 }, // Extra bottom padding on mobile for fixed bottom nav (56px height + 14px margin)
          overflowY: activeTab === 'chat' ? 'hidden' : undefined,
        }}
      >
        <Box sx={{ 
          ...(isMobile ? { maxWidth: '100%' } : activeTab === 'chat' ? { maxWidth: '100%' } : { maxWidth: 1200 }),
          mx: 'auto', 
          width: '100%', 
          minWidth: 0,
          boxSizing: 'border-box',
        }}>
          {/* Mobile: Personalised greeting + Overview audio guide — Calculator tab only */}
          {isMobile && activeTab === 'calculator' && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2,
                py: 1.25,
                mb: 1.5,
                borderRadius: 'xl',
                background: 'linear-gradient(135deg, rgba(102,126,234,0.1) 0%, rgba(118,75,162,0.1) 100%)',
                border: '1px solid',
                borderColor: 'primary.outlinedBorder',
              }}
            >
              <Box>
                <Typography
                  level="title-sm"
                  sx={{ fontWeight: 700, fontSize: '1.05rem', letterSpacing: '0.01em' }}
                >
                  {(() => {
                    const h = new Date().getHours();
                    if (h >= 5 && h < 12) return '🌅 Good morning';
                    if (h >= 12 && h < 17) return '☀️ Good afternoon';
                    if (h >= 17 && h < 21) return '🌆 Good evening';
                    return '🌙 Good night';
                  })()}, {userLabel.split(/[\s@]/)[0]}!
                </Typography>
                <Typography level="body-xs" sx={{ color: 'text.tertiary', mt: 0.25 }}>
                  {new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })}
                </Typography>
              </Box>
              {homeAudioGuide && (
                <Box sx={{ flexShrink: 0, ml: 1 }}>{homeAudioGuide}</Box>
              )}
            </Box>
          )}
          {children}
        </Box>
      </Box>

      {/* Mobile Navigation Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{
          '& .MuiDrawer-content': {
            width: 280,
            bgcolor: 'background.surface',
          }
        }}
      >
        <ModalClose onClick={() => setDrawerOpen(false)} />
        
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography level="h4" sx={{ fontWeight: 700, color: '#b83050' }}>
            Settings
          </Typography>
          {chartName && (
            <Typography level="body-sm" sx={{ color: 'text.secondary', mt: 0.5 }}>
              {chartName}'s Chart
            </Typography>
          )}
        </Box>

        <List sx={{ py: 1 }}>
          {onOpenMySubscription && (
            <ListItem>
              <ListItemButton
                onClick={() => { onOpenMySubscription(); setDrawerOpen(false); }}
                sx={{ borderRadius: 'md' }}
              >
                <ListItemDecorator>
                  <WorkspacePremiumIcon sx={{ fontSize: '1.1rem', color: '#DAA520' }} />
                </ListItemDecorator>
                {subscriptionLabel ?? 'My Subscription'}
              </ListItemButton>
            </ListItem>
          )}

          {onOpenWhatsAppOptIn && (
            <ListItem>
              <ListItemButton
                onClick={() => {
                  onOpenWhatsAppOptIn();
                  setDrawerOpen(false);
                }}
                sx={{ borderRadius: 'md' }}
              >
                <ListItemDecorator>
                  <WhatsAppIcon sx={{ fontSize: '1.1rem', color: '#25D366' }} />
                </ListItemDecorator>
                WhatsApp Notifications
              </ListItemButton>
            </ListItem>
          )}

          <ListItem>
            <ListItemButton
              onClick={() => {
                onTabChange('family');
                setDrawerOpen(false);
              }}
              sx={{ borderRadius: 'md' }}
            >
              <ListItemDecorator>
                <GroupIcon fontSize="small" />
              </ListItemDecorator>
              Family Manager
            </ListItemButton>
          </ListItem>
          
          {isAdmin && (
            <ListItem>
              <ListItemButton
                onClick={() => {
                  onTabChange('admin');
                  setDrawerOpen(false);
                }}
                sx={{ borderRadius: 'md' }}
              >
                <ListItemDecorator>
                  <AdminPanelSettingsIcon fontSize="small" />
                </ListItemDecorator>
                Admin Console
              </ListItemButton>
            </ListItem>
          )}

          {isAdmin && onGenerateReport && (
            <ListItem>
              <ListItemButton
                onClick={() => {
                  onGenerateReport();
                  setDrawerOpen(false);
                }}
                sx={{ borderRadius: 'md' }}
              >
                <ListItemDecorator>
                  <DescriptionIcon fontSize="small" />
                </ListItemDecorator>
                Generate Report (Beta)
              </ListItemButton>
            </ListItem>
          )}

          {onRefreshAccess && (
            <ListItem>
              <ListItemButton
                onClick={() => {
                  onRefreshAccess();
                  setDrawerOpen(false);
                }}
                sx={{ borderRadius: 'md' }}
              >
                <ListItemDecorator>
                  <RefreshIcon fontSize="small" />
                </ListItemDecorator>
                Refresh Access
              </ListItemButton>
            </ListItem>
          )}

          {onBuyCredits && chatCreditsRemaining !== null && chatCreditsRemaining !== undefined && (
            <ListItem>
              <ListItemButton
                onClick={() => {
                  onBuyCredits();
                  setDrawerOpen(false);
                }}
                sx={{ borderRadius: 'md' }}
              >
                <ListItemDecorator>
                  <ChatIcon fontSize="small" />
                </ListItemDecorator>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <span>Buy AI Credits</span>
                  <Chip
                    size="sm"
                    variant="soft"
                    color={chatCreditsRemaining === 0 ? 'warning' : chatCreditsRemaining <= 5 ? 'warning' : 'success'}
                    sx={{ fontSize: '0.7rem', fontWeight: 700 }}
                  >
                    {chatCreditsRemaining === 999999 ? '∞' : chatCreditsRemaining} left
                  </Chip>
                </Box>
              </ListItemButton>
            </ListItem>
          )}

          <ListItem>
            <ListItemButton
              onClick={() => supabase.auth.signOut()}
              sx={{ borderRadius: 'md', color: 'danger.500' }}
            >
              <ListItemDecorator>
                <LogoutIcon sx={{ fontSize: '1.1rem' }} />
              </ListItemDecorator>
              Sign Out
            </ListItemButton>
          </ListItem>
        </List>
      </Drawer>

      {/* Mobile Bottom Navigation Bar */}
      {isMobile && (
        <Box
          data-audio-cue="home-bottom-nav"
          data-audio-scroll="bottom"
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 10000,
            bgcolor: 'background.surface',
            borderTop: '1px solid',
            borderColor: 'divider',
            boxShadow: 'lg',
            pb: 'env(safe-area-inset-bottom, 0)',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-around',
              alignItems: 'center',
              px: 1,
              py: 1,
            }}
          >
            {/* Ask Shreem (Chat) */}
            <IconButton
              variant={activeTab === 'chat' ? 'soft' : 'plain'}
              color={activeTab === 'chat' ? 'primary' : 'neutral'}
              onClick={() => {
                if (isMobile && onOpenMobileChat) {
                  onOpenMobileChat();
                } else {
                  onTabChange('chat');
                }
              }}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.25,
                minWidth: 60,
                height: 56,
                borderRadius: 'md',
              }}
            >
              <Box
                component="img"
                src="/Logo.png"
                alt=""
                sx={{
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  opacity: activeTab === 'chat' ? 1 : 0.7,
                }}
              />
              <Typography level="body-xs" sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Ask Shreem</Typography>
            </IconButton>

            {/* Dashboard (was Home) */}
            <IconButton
              variant={activeTab === 'calculator' ? 'soft' : 'plain'}
              color={activeTab === 'calculator' ? 'primary' : 'neutral'}
              disabled={!canUseCalculator}
              onClick={() => onTabChange('calculator')}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.25,
                minWidth: 60,
                height: 56,
                borderRadius: 'md',
              }}
            >
              <InsightsIcon sx={{ fontSize: '1.4rem' }} />
              <Typography level="body-xs" sx={{ fontSize: '0.85rem', fontWeight: 600 }}>My Chart</Typography>
            </IconButton>

            {/* Current Period */}
            <IconButton
              variant={activeTab === 'dasha' ? 'soft' : 'plain'}
              color={activeTab === 'dasha' ? 'primary' : 'neutral'}
              onClick={() => onTabChange('dasha')}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.25,
                minWidth: 60,
                height: 56,
                borderRadius: 'md',
              }}
            >
              <TimelineIcon sx={{ fontSize: '1.4rem' }} />
              <Typography level="body-xs" sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Life Timing</Typography>
            </IconButton>

            {/* Yogas & Predictions */}
            <IconButton
              variant={activeTab === 'feeds' ? 'soft' : 'plain'}
              color={activeTab === 'feeds' ? 'primary' : 'neutral'}
              onClick={() => onTabChange('feeds')}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.25,
                minWidth: 60,
                height: 56,
                borderRadius: 'md',
              }}
            >
              <AutoAwesomeIcon sx={{ fontSize: '1.4rem' }} />
              <Typography level="body-xs" sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Yogas</Typography>
            </IconButton>

            {/* Detailed Synopsis */}
            <IconButton
              variant={activeTab === 'mychart' ? 'soft' : 'plain'}
              color={activeTab === 'mychart' ? 'primary' : 'neutral'}
              onClick={() => onTabChange('mychart')}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.25,
                minWidth: 60,
                height: 56,
                borderRadius: 'md',
              }}
            >
              <DescriptionIcon sx={{ fontSize: '1.4rem' }} />
              <Typography level="body-xs" sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Deep Insights</Typography>
            </IconButton>
          </Box>
        </Box>
      )}
    </Box>
  );
}
// ===== END FILE: src/components/AppShell.tsx =====
